import React, {useCallback} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {useSwipeable, SwipeCallback} from 'react-swipeable'

import {Container} from './styles'

const propTypes = {
  onSwiping: PropTypes.func,
  onSwiped: PropTypes.func,
  onSwipeStart: PropTypes.func,
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  innerRef: PropTypes.any,
  containerOverflow: PropTypes.bool,
}
const defaultProps = {
  onSwiping: undefined,
  onSwiped: undefined,
  onSwipeStart: undefined,
  innerRef: undefined,
  containerOverflow: false,
}

export type SwipeableContainerProps = InferPropTypes<typeof propTypes, typeof defaultProps> & {
  onSwiping?: SwipeCallback
  onSwiped?: SwipeCallback
  onSwipeStart?: SwipeCallback
} & React.ComponentProps<'div'> & {innerRef?: React.Ref<HTMLDivElement>}

const SwipeableContainer: React.FC<SwipeableContainerProps> = ({
  onSwiping,
  onSwiped,
  onSwipeStart,
  innerRef,
  children,
  ...restProps
}) => {
  const swipeHandler = useSwipeable({
    trackMouse: true,
    trackTouch: true,
    onSwiping,
    onSwiped,
    onSwipeStart,
  })

  const setRef = useCallback(
    (el: HTMLDivElement) => {
      swipeHandler.ref(el)

      if (typeof innerRef === 'function') {
        innerRef(el)
      } else if (typeof innerRef !== 'undefined') {
        // eslint-disable-next-line no-param-reassign
        innerRef.current = el
      }
    },
    [innerRef, swipeHandler]
  )

  return (
    <Container {...restProps} {...swipeHandler} ref={setRef}>
      {children}
    </Container>
  )
}

SwipeableContainer.propTypes = propTypes
SwipeableContainer.defaultProps = defaultProps

export default React.forwardRef<HTMLDivElement, SwipeableContainerProps>((props, ref) => (
  <SwipeableContainer {...props} innerRef={ref} />
))
